import { ArrowDown, Lock } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@nucleus-commerce/ui/components/avatar';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { DEFAULT_GROUP_WINDOW_MS, groupThreadItems, type ThreadItem } from './group-items';

export interface MessageThreadLabels {
  /** Accessible name of the scrolling log. */
  thread: string;
  loadOlder: string;
  jumpToLatest: string;
  /** Read out next to the lock on internal groups. */
  internal: string;
}

export interface MessageThreadProps<T extends ThreadItem> {
  /** Oldest first. */
  items: readonly T[];
  /** The body of one item: text, a card, an event line. */
  renderItem: (item: T) => ReactNode;
  /** Small line under an item, e.g. time and read ticks. Shown under the last item of a group. */
  renderMeta?: (item: T) => ReactNode;
  /** Text of the separator before the first group of a day. */
  formatDay: (iso: string) => string;
  labels: MessageThreadLabels;
  /** Above the log, e.g. the conversation header. */
  header?: ReactNode;
  /** Between the log and the composer: typing indicator, presence line, banners. */
  status?: ReactNode;
  /** The input area; the thread never owns what is typed. */
  composer?: ReactNode;
  /** Shown instead of the log when there are no items. */
  empty?: ReactNode;
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  groupWindowMs?: number;
  className?: string;
}

// Distance from the bottom (px) inside which the log still counts as "at the latest message".
const STICK_THRESHOLD = 80;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0], parts.at(-1)] : [parts[0]];
  return letters
    .map((part) => part?.[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * A chat log with a composer slot. Items group by author, tone and day; `internal` groups get a
 * distinct surface and a lock. The log keeps the newest message in view while the reader is at the
 * bottom, keeps its place when older pages are prepended, and offers a jump back otherwise.
 *
 * Off-screen groups skip layout and paint through `content-visibility: auto`, which keeps long
 * threads cheap without a virtualiser that would fight variable heights and stick-to-bottom.
 */
export function MessageThread<T extends ThreadItem>({
  items,
  renderItem,
  renderMeta,
  formatDay,
  labels,
  header,
  status,
  composer,
  empty,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
  groupWindowMs = DEFAULT_GROUP_WINDOW_MS,
  className,
}: MessageThreadProps<T>) {
  const groups = useMemo(() => groupThreadItems(items, groupWindowMs), [items, groupWindowMs]);
  const logRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const firstId = items[0]?.id;
  const lastId = items.at(-1)?.id;
  const lastAlign = items.at(-1)?.align;
  const previous = useRef<{ firstId?: string; lastId?: string; height: number }>({ height: 0 });

  useLayoutEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const before = previous.current;
    const prepended =
      before.firstId !== undefined && firstId !== before.firstId && lastId === before.lastId;
    if (prepended) {
      // Older messages went in above: keep the reader on the message they were looking at.
      log.scrollTop += log.scrollHeight - before.height;
    } else if (lastId !== before.lastId) {
      // A message we sent always scrolls; someone else's only while the reader is at the bottom.
      if (atBottomRef.current || lastAlign === 'end' || before.lastId === undefined) {
        log.scrollTop = log.scrollHeight;
        setShowJump(false);
      } else {
        setShowJump(true);
      }
    }
    previous.current = { firstId, lastId, height: log.scrollHeight };
  }, [firstId, lastId, lastAlign]);

  const onScroll = () => {
    const log = logRef.current;
    if (!log) return;
    const atBottom = log.scrollHeight - log.scrollTop - log.clientHeight <= STICK_THRESHOLD;
    atBottomRef.current = atBottom;
    if (atBottom) setShowJump(false);
  };

  const jumpToLatest = () => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
    atBottomRef.current = true;
    setShowJump(false);
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      {header}
      <div className="relative min-h-0 flex-1">
        <div
          ref={logRef}
          role="log"
          aria-label={labels.thread}
          aria-live="polite"
          aria-relevant="additions"
          onScroll={onScroll}
          className="h-full overflow-y-auto px-4 py-3"
        >
          {hasOlder && onLoadOlder ? (
            <div className="flex justify-center pb-3">
              <Button variant="ghost" size="sm" onClick={onLoadOlder} disabled={loadingOlder}>
                {loadingOlder ? <Spinner /> : null}
                {labels.loadOlder}
              </Button>
            </div>
          ) : null}
          {groups.length === 0 ? empty : null}
          {groups.map((group, index) => {
            const newDay = index === 0 || groups[index - 1]?.day !== group.day;
            const first = group.items[0];
            const last = group.items.at(-1);
            return (
              <section
                key={group.key}
                className="pb-3"
                style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 96px' }}
              >
                {newDay && first ? (
                  <div className="text-muted-foreground flex items-center gap-3 py-2 text-xs">
                    <span className="bg-border h-px flex-1" aria-hidden />
                    <span>{formatDay(first.createdAt)}</span>
                    <span className="bg-border h-px flex-1" aria-hidden />
                  </div>
                ) : null}
                {group.align === 'center' ? (
                  <div className="text-muted-foreground space-y-1 text-center text-xs">
                    {group.items.map((item) => (
                      <div key={item.id} data-thread-item={item.id}>
                        {renderItem(item)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={cn(
                      'flex items-end gap-2',
                      group.align === 'end' && 'flex-row-reverse',
                    )}
                  >
                    {first ? (
                      <Avatar className="size-7 shrink-0">
                        {first.avatarUrl ? <AvatarImage src={first.avatarUrl} alt="" /> : null}
                        <AvatarFallback className="text-[10px]">
                          {initials(first.authorName)}
                        </AvatarFallback>
                      </Avatar>
                    ) : null}
                    <div
                      className={cn(
                        'flex max-w-[78%] min-w-0 flex-col gap-1',
                        group.align === 'end' ? 'items-end' : 'items-start',
                      )}
                    >
                      <div className="text-muted-foreground flex items-center gap-1 px-1 text-xs">
                        {group.tone === 'internal' ? (
                          <>
                            <Lock className="size-3" aria-hidden />
                            <span className="sr-only">{labels.internal}</span>
                          </>
                        ) : null}
                        <span className="text-foreground font-medium">{first?.authorName}</span>
                      </div>
                      {group.items.map((item) => (
                        <div
                          key={item.id}
                          data-thread-item={item.id}
                          className={cn(
                            'rounded-2xl px-3 py-2 text-sm [overflow-wrap:anywhere] break-words',
                            group.tone === 'internal'
                              ? 'border-warning/50 bg-warning/15 text-foreground border border-dashed'
                              : group.align === 'end'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-foreground',
                          )}
                        >
                          {renderItem(item)}
                        </div>
                      ))}
                      {renderMeta && last ? (
                        <div className="text-muted-foreground px-1 text-[11px]">
                          {renderMeta(last)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
        {showJump ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={jumpToLatest}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 shadow-md"
          >
            <ArrowDown aria-hidden />
            {labels.jumpToLatest}
          </Button>
        ) : null}
      </div>
      {status}
      {composer}
    </div>
  );
}
